<template>

  <div v-if="errors && !readonly" class="alert alert-danger alert-dismissible fade show" role="alert">
    <div class="d-flex align-items-start">
      <svg class="icon icon-danger me-2">
        <use href="/bootstrap-italia/dist/svg/sprites.svg#it-close-circle"></use>
      </svg>
      <div>
        <h5 class="alert-heading">Si sono verificati degli errori</h5>
        <ul class="mb-0 ps-3">
          <li v-for="(errorList, field) in errors" :key="field">
            {{ errorList[0] }}
          </li>
        </ul>
      </div>
    </div>
    <button type="button" class="btn-close" @click="$emit('update:errors', null)" aria-label="Chiudi"></button>
  </div>


  <div v-if="successMessage && !readonly" class="alert alert-success alert-dismissible fade show" role="alert">
    <div class="d-flex align-items-center">
      <svg class="icon icon-success me-2">
        <use href="/bootstrap-italia/dist/svg/sprites.svg#it-check-circle"></use>
      </svg>
      <span>{{ successMessage }}</span>
    </div>
    <button type="button" class="btn-close" @click="$emit('update:successMessage', null)" aria-label="Chiudi"></button>
  </div>


  <form v-if="!readonly" @submit.prevent="onSubmit" class="needs-validation" novalidate>
    <div class="row g-4">

      <div class="col-md-6">
        <div class="form-group">
          <label class="form-label" for="topicsDropdown">Argomenti *</label>
          <div class="dropdown">
            <button id="topicsDropdown" class="btn btn-dropdown w-100 text-start" type="button"
              data-bs-toggle="dropdown">
              {{ selectedTopicsLabel }}
            </button>
            <div class="dropdown-menu w-100 p-3" style="max-height: 250px; overflow-y: auto;">
              <div v-for="topic in topics" :key="topic.id" class="form-check">
                <input class="form-check-input" type="checkbox" :id="'topic-' + topic.id" :value="topic.id"
                  v-model="formData.topics" />
                <label class="form-check-label" :for="'topic-' + topic.id">
                  {{ topic.title }}
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>


      <div class="col-md-6">
        <div class="form-group">
          <label class="form-label" for="articlesDropdown">Articoli</label>
          <div class="dropdown">
            <button id="articlesDropdown" class="btn btn-dropdown w-100 text-start" type="button"
              data-bs-toggle="dropdown">
              {{ selectedArticlesLabel }}
            </button>
            <div class="dropdown-menu w-100 p-3" style="max-height: 250px; overflow-y: auto;">
              <div v-for="article in articles" :key="article.id" class="form-check">
                <input class="form-check-input" type="checkbox" :id="'article-' + article.id" :value="article.id"
                  v-model="formData.articles" />
                <label class="form-check-label" :for="'article-' + article.id">
                  <!--{{ article.title }}-->
                  {{ article.id }}
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>


      <div class="col-md-6">
        <div class="form-group">
          <label class="form-label" for="legislationDropdown">Legislazione *</label>
          <div class="dropdown">
            <button id="legislationDropdown" class="btn btn-dropdown w-100 text-start" type="button"
              data-bs-toggle="dropdown">
              {{ selectedLegislationLabel }}
            </button>
            <div class="dropdown-menu w-100 p-3" style="max-height: 250px; overflow-y: auto;">
              <div v-for="leg in legislations" :key="leg.id" class="form-check">
                <input class="form-check-input" type="radio" :id="'leg-' + leg.id" :value="leg.id"
                  v-model="formData.legislation_id" />
                <label class="form-check-label" :for="'leg-' + leg.id">
                  {{ leg.title }}
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>


      <div class="col-md-12">
        <div class="form-group">
          <label class="form-label" for="objectInput">
            Oggetto del quesito
            <small class="text-secondary ms-2">(max {{ maxObjectLength }} caratteri)</small>
          </label>
          <input id="objectInput" type="text" class="form-control" v-model="formData.object"
            :maxlength="maxObjectLength" placeholder="Inserisci l'oggetto" required />
          <small v-if="formData.object" class="text-secondary">
            Caratteri rimanenti: {{ remainingObjectChars }}
          </small>
        </div>
      </div>


      <div class="col-md-12">
        <div class="form-group">
          <label class="form-label" for="textInput">
            Testo del quesito
            <small class="text-secondary ms-2">(max {{ maxTextLength }} caratteri)</small>
          </label>
          <textarea id="textInput" class="form-control" v-model="formData.text" :maxlength="maxTextLength" rows="5"
            placeholder="Inserisci il testo del quesito"></textarea>
          <small v-if="formData.text" class="text-secondary">
            Caratteri rimanenti: {{ remainingTextChars }}
          </small>
        </div>
      </div>


      <div class="col-12 text-end mt-4">
        <button type="submit" class="btn btn-primary btn-icon" :disabled="!isFormValid">
          <span class="icon icon-sm me-2">
            <svg class="icon">
              <use href="/bootstrap-italia/dist/svg/sprites.svg#it-save"></use>
            </svg>
          </span>
          Salva Quesito
        </button>
      </div>
    </div>
  </form>


  <div v-else class="question-detail border rounded p-3 bg-white shadow-sm">
    <div class="detail-section">
      <h6 class="section-title">Argomenti</h6>
      <p v-if="formData.topics && formData.topics.length" class="form-control-plaintext border rounded p-2 bg-light">
        {{formData.topics?.map(t => t.title).join(', ') || '—'}}
      </p>
      <p v-else class="form-control-plaintext border rounded p-2 bg-light">—</p>
    </div>

    <div class="detail-section">
      <h6 class="section-title">Articoli</h6>

      <p v-if="formData.articles && formData.articles.length"
        class="form-control-plaintext border rounded p-2 bg-light">
        <template v-for="(a, idx) in formData.articles" :key="a.id">
          <span :title="a.title || ''">{{ a.title ? formatArticleTitle(a.title) : `Art. ${a.id}` }}</span>
          <span v-if="idx < formData.articles.length - 1">, </span>
        </template>
      </p>

      <p v-else class="form-control-plaintext border rounded p-2 bg-light">—</p>
    </div>

    <div class="detail-section">
      <h6 class="section-title">Legislazione</h6>
      <p class="form-control-plaintext border rounded p-2 bg-light">
        {{ getLegislationTitle(formData.legislation_id) || '—' }}
      </p>
    </div>

    <div class="detail-section">
      <h6 class="section-title">Oggetto</h6>
      <p class="form-control-plaintext border rounded p-2 bg-light">
        {{ formData.object || '—' }}
      </p>
    </div>

    <div class="detail-section">
      <h6 class="section-title">Testo</h6>
      <div class="border rounded p-3 bg-light" :class="{ 'scroll-enabled': stripHtml(formData.text).length > 1000 }">
        {{ stripHtml(formData.originalText) }}
      </div>
    </div>

    <div class="detail-section">
      <h6 class="section-title">Risposta</h6>
      <div class="form-control-plaintext border rounded p-2 bg-light"
        :class="{ 'scroll-enabled': stripHtml(formData.answer).length > 1000 }">
       {{ formData.state_id === 'ASSEGNATO' ? '-' : stripHtml(formData.answer) }}
      </div>
    </div>

    <!--<div class="detail-section">
      <h6 class="section-title">Parere</h6>
      <div class="form-control-plaintext border rounded p-2 bg-light"
        :class="{ 'scroll-enabled': stripHtml(formData.opinion).length > 1000 }">
        {{ stripHtml(formData.opinion) }}
      </div>
    </div>
  --> <!--visualizzazione del parere temporaneamente nascosta-->
   </div>
</template>


<script>
export default {
  name: "QuestionForm",

  props: {
    modelValue: {
      type: Object,
      required: true,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    errors: {
      type: Object,
      default: null,
    },
    successMessage: {
      type: String,
      default: null,
    },
    onSubmit: {
      type: Function,
      required: false,
    },
  },

  data() {
    return {
      topics: [],
      legislations: [],
      articles: [],
      maxObjectLength: 120,
      maxTextLength: 1500,
      answer: null
    };
  },

  watch: {
    'formData.legislation_id': async function (newLegId) {
      if (!newLegId) {
        this.articles = [];
        return;
      }
      try {
        const response = await this.$root.$api.get('/articles/bylegislation', {
          params: { legislation_id: newLegId }
        });
        this.articles = response.data;
      } catch (error) {
        console.error("Errore caricamento articoli:", error);
        this.articles = [];
      }
    }
  },

  computed: {
    formData: {
      get() {
        return this.modelValue;
      },
      set(newValue) {
        this.$emit("update:modelValue", newValue);
      },
    },
    remainingObjectChars() {
      return this.maxObjectLength - (this.formData.object?.length || 0);
    },
    remainingTextChars() {
      return this.maxTextLength - (this.formData.text?.length || 0);
    },
    selectedTopicsLabel() {
      if (!this.formData.topics || this.formData.topics.length === 0)
        return "Seleziona argomenti";
      if (this.formData.topics.length === 1) {
        const topic = this.topics.find((t) => t.id === this.formData.topics[0]);
        return topic ? topic.title : "1 argomento";
      }
      return `Selezione multipla (${this.formData.topics.length})`;
    },
    selectedLegislationLabel() {
      if (!this.formData.legislation_id) return "Seleziona legislazione";
      const leg = this.legislations.find(l => l.id === this.formData.legislation_id);
      return leg ? leg.title : "Seleziona legislazione";
    },
    selectedArticlesLabel() {
      if (!this.formData.articles || this.formData.articles.length === 0)
        return "Seleziona articoli";
      if (this.formData.articles.length === 1) {
        const art = this.articles.find(a => a.id === this.formData.articles[0]);
        return art ? art.title : "1 articolo";
      }
      return `Selezione multipla (${this.formData.articles.length})`;
    },
    isFormValid() {
      return (
        this.formData.object?.trim().length > 0 &&
        this.formData.text?.trim().length > 0 &&
        this.formData.topics?.length > 0 &&
        this.formData.legislation_id !== null
      );
    },
  },

  async mounted() {
    try {
      const response = await this.$root.$api.get("/questions/topics");
      this.topics = response.data;

      //const legislationResponse = await this.$root.$api.get("/questions/legislations");
      //const legislationResponse = await this.$root.fetchDlgs();
      this.legislations = Object.values(this.$root.dlgs || {});
    } catch (error) {
      console.error("Errore caricamento argomenti:", error);
    }
  },

  methods: {
    stripHtml(html) {
      if (!html) return '—';

      const div = document.createElement('div');
      div.innerHTML = html;
      return div.textContent || div.innerText || '';
    },
    getTopicName(id) {
      const topic = this.topics.find((t) => t.id === id);
      return topic ? topic.title : "—";
    },
    getLegislationTitle(id) {
      const leg = this.legislations.find((l) => l.id === id);
      return leg ? leg.title : "—";
    },
    formatArticleTitle(title) {
      if (!title) return '';
      /**const artDotMatch = title.match(/^Art\.\s*(\d+)/i);
      if (artDotMatch) return `Art.${artDotMatch[1]}`;*/
      const articoloMatch = title.match(/^Articolo\s+(\d+)/i);
      if (articoloMatch) return `Articolo ${articoloMatch[1]}`;
      return title;
    }
  },
};
</script>

<style scoped>
.readonly-view p,
.readonly-view div {
  font-size: 0.95rem;
}

.readonly-view .badge {
  font-size: 0.85rem;
}

/** .question-detail {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
}*/

.question-detail {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  max-height: 40rem;
  overflow-y: auto;
  padding: 1rem;
}

.detail-section {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.section-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: #495057;
  border-bottom: 1px solid #dee2e6;
  padding-bottom: 0.25rem;
  margin-bottom: 0.5rem;
}

.badge {
  font-size: 0.85rem;
}

.border.rounded {
  border-radius: 0.5rem;
}

.bg-light {
  background-color: #f8f9fa !important;
}

.scroll-enabled {
  max-height: 300px !important;
  overflow-y: auto !important;
}

.form-control-plaintext {
  white-space: pre-wrap;
  word-break: break-word;
}

/** 

Se vogliamo impostare lo scrolling all'interno del popup

.question-detail {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    max-height: 600px;        
    overflow-y: auto;        
    padding: 1rem;
}


.detail-section { Sezioni dettagli, scrollabile se contenuto lungo
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}


.section-title {
    font-size: 0.9rem;
    font-weight: 600;
    color: #495057;
    border-bottom: 1px solid #dee2e6;
    padding-bottom: 0.25rem;
    margin-bottom: 0.5rem;
}


.badge {
    font-size: 0.85rem;
    word-break: break-word; 
}

.form-control-plaintext, 
.border.rounded {
    white-space: pre-wrap;
    word-break: break-word;
}

.scroll-enabled {
    max-height: 250px;       
    overflow-y: auto;
}

*/
</style>